import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  User,
  AuthContextType,
  ApiResponse,
  AuthApiResponse,
} from "../types/auth";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

// ヘルパー関数: 認証ヘッダーを取得
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("auth_token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // ページ読み込み時にユーザー情報をチェック
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: "GET",
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data: ApiResponse<AuthApiResponse> = await response.json();
        if (data.success && data.data?.user) {
          setUser(data.data.user);
        } else {
          setUser(null);
          localStorage.removeItem("auth_token");
        }
      } else {
        setUser(null);
        localStorage.removeItem("auth_token");
      }
    } catch (error) {
      setUser(null);
      localStorage.removeItem("auth_token");
    } finally {
      setLoading(false);
    }
  };

  const login = () => {
    // Google認証ページにリダイレクト
    window.location.href = `${API_BASE_URL}/api/auth/google`;
  };

  const logout = async () => {
    try {
      setLoading(true);
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });

      setUser(null);
      localStorage.removeItem("auth_token");
      // aboutページにリダイレクト
      window.location.href = "/about";
    } catch (error) {
      setError("ログアウトに失敗しました");
      localStorage.removeItem("auth_token");
    } finally {
      setLoading(false);
    }
  };

  const refreshAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data: ApiResponse<AuthApiResponse> = await response.json();
        if (data.success && data.data?.user) {
          setUser(data.data.user);
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshAuth,
    checkAuth,
    clearError,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
